/** /host/sessions/:id — the facilitator console. */
import { formatDateTime } from "@shared/i18n";
import type { FacilitatorCommand, Seat, ServerEvent, SessionRecord, SessionSettings } from "@shared/protocol";
import type { DecisionNode, Scenario, ScenarioNode } from "@shared/scenario";
import { nodeSuccessors } from "@shared/scenario";
import {
  MAX_SIMULATED_PER_SESSION,
  SIMULATION_TEMPERAMENTS,
  type SimulationPace,
  type SimulationTemperament,
} from "@shared/simulation";
import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import { Dimensions, PhoneCall, SimMark, TimerRing, VoteBars } from "../components/game";
import { ConnectionBanner, Page } from "../components/Header";
import { hasIcon, Icon } from "../components/Icon";
import { Button, Card, CopyButton, Dialog, Field, Pill, QualityPill, Spinner, toast } from "../components/ui";
import { useI18n } from "../i18n";
import { type ApiError, del, get, patch, post } from "../lib/api";
import { useRoom, useServerClock } from "../lib/room";
import { useScenario } from "../lib/scenario";
import { navigate } from "../router";
import { type FacilitatorInfo, HostNav, SettingsForm } from "./HostDashboard";

interface Detail {
  session: SessionRecord;
  links: Record<string, string>;
  seats: (Seat & { link: string })[];
}

export function HostConsole({
  id,
  facilitator,
  onSignOut,
}: {
  id: string;
  facilitator: FacilitatorInfo;
  onSignOut: () => void;
}) {
  const { t, locale } = useI18n();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [wsPath, setWsPath] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await get<Detail>(`/api/sessions/${id}`);
      setDetail(d);
    } catch (e) {
      setError((e as ApiError).status === 404 ? t("error.notFound") : t("error.generic"));
    }
  }, [id, t]);

  useEffect(() => {
    load();
    post<{ wsPath: string }>(`/api/sessions/${id}/ws-token`)
      .then((r) => setWsPath(r.wsPath))
      .catch(() => setWsPath(null));
  }, [id, load]);

  const room = useRoom(wsPath);
  const { state, status } = room;
  const { data } = useScenario<Scenario>(
    detail?.session.scenarioId ?? null,
    locale,
    "full",
    detail?.session.code ?? null,
  );
  const scenario = data?.scenario ?? null;

  const [httpFallback, setHttpFallback] = useState(false);
  useEffect(() => {
    if (status === "reconnecting") {
      const id2 = setTimeout(() => setHttpFallback(true), 6000);
      return () => clearTimeout(id2);
    }
    if (status === "open") setHttpFallback(false);
  }, [status]);

  const command = useCallback(
    async (cmd: FacilitatorCommand) => {
      if (status === "open" && !httpFallback) {
        room.send({ type: "cmd", cmd });
        return;
      }
      try {
        await post(`/api/sessions/${id}/command`, cmd);
      } catch (e) {
        toast((e as ApiError).message, "error");
      }
    },
    [room, status, httpFallback, id],
  );

  useEffect(
    () =>
      room.onEvent((e: ServerEvent) => {
        if (e.type === "toast") toast(t(e.key, e.params), e.level);
        if (e.type === "ended") load();
      }),
    [room, t, load],
  );

  if (error) {
    return (
      <Page>
        <HostNav facilitator={facilitator} onSignOut={onSignOut} />
        <main id="main">
          <p role="alert" class="error-text">
            {error}
          </p>
          <Button onClick={() => navigate("/host")}>{t("action.back")}</Button>
        </main>
      </Page>
    );
  }
  if (!detail || !scenario) {
    return (
      <Page>
        <HostNav facilitator={facilitator} onSignOut={onSignOut} />
        <main id="main" style={{ paddingBlock: "var(--s-8)" }}>
          <Spinner />
        </main>
      </Page>
    );
  }

  return (
    <Page wide>
      <HostNav facilitator={facilitator} onSignOut={onSignOut} />
      <main id="main" class="stack" style={{ paddingBottom: "var(--s-12)" }}>
        <div class="row between">
          <div class="row">
            <Button variant="ghost" size="sm" icon="arrow-left" onClick={() => navigate("/host")}>
              {t("host.dash.title")}
            </Button>
            <h1 style={{ margin: 0, fontSize: "var(--text-xl)" }}>{detail.session.title}</h1>
            <span class="code-display fg-2">{detail.session.code}</span>
            {state ? (
              <Pill tone={state.status === "live" ? "live" : "accent"}>{t(`status.${state.status}`)}</Pill>
            ) : null}
          </div>
          <div class="row">
            <a href={detail.links.screen} target="_blank" rel="noopener" class="btn secondary sm">
              <Icon name="monitor" /> {t("host.console.openScreen")}
            </a>
            <a href={detail.links.report} target="_blank" rel="noopener" class="btn secondary sm">
              <Icon name="file-text" /> {t("host.console.report")}
            </a>
          </div>
        </div>
        <ConnectionBanner status={status} />
        {httpFallback ? (
          <div class="card signal tight small">
            <Icon name="info" /> {t("host.console.wsFallback")}
          </div>
        ) : null}
        {state ? (
          <div class="console">
            <Stage state={state} scenario={scenario} command={command} room={room} />
            <Side state={state} scenario={scenario} command={command} detail={detail} reload={load} />
          </div>
        ) : (
          <Spinner label={t("status.connecting")} />
        )}
      </main>
    </Page>
  );
}

type RoomHandle = ReturnType<typeof useRoom>;
type S = NonNullable<RoomHandle["state"]>;
type Cmd = (cmd: FacilitatorCommand) => Promise<void>;

function Stage({
  state,
  scenario,
  command,
  room,
}: {
  state: S;
  scenario: Scenario;
  command: Cmd;
  room: RoomHandle;
}) {
  const { t } = useI18n();
  const nodeId = state.nodeId;
  const node = nodeId ? scenario.nodes[nodeId] : null;
  const vote = state.vote && state.vote.nodeId === nodeId ? state.vote : null;
  const poll = state.poll && state.poll.nodeId === nodeId ? state.poll : null;
  const now = useServerClock(room, Boolean((vote && !vote.result) || (poll && !poll.closed)));
  const [confirm, setConfirm] = useState<null | "end" | "reopen">(null);
  const [forceOpen, setForceOpen] = useState(false);

  const primaryLabel = (() => {
    if (state.status === "lobby") return t("host.console.start");
    if (!node) return t("host.console.next");
    if (node.type === "call" && state.revealedLines < node.transcript.length)
      return t("host.console.revealLine");
    if (node.type === "decision") {
      if (!vote) return t("host.console.openVote");
      if (!vote.result) return t("host.console.closeVote");
    }
    if (node.type === "poll" && poll && !poll.closed) return t("host.console.closeVote");
    return t("host.console.next");
  })();

  const primary = () => {
    if (state.status === "lobby") return command({ action: "start" });
    if (node?.type === "call" && state.revealedLines < node.transcript.length)
      return command({ action: "revealLine" });
    return command({ action: "next" });
  };

  return (
    <section class="stack">
      <Card class="stack">
        <div class="row between">
          <span class="eyebrow">{t("host.console.currentNode")}</span>
          {node ? <span class="node-type">{node.type}</span> : null}
          {state.paused ? <Pill tone="signal">{t("status.paused")}</Pill> : null}
        </div>
        {state.status === "lobby" ? (
          <>
            <h2>{t("status.lobby")}</h2>
            <p class="fg-2">{scenario.summary}</p>
            <p class="muted small">
              {t("host.console.players")}: {state.playerCount}
            </p>
          </>
        ) : node ? (
          <NodeView node={node} state={state} vote={vote} poll={poll} now={now} scenario={scenario} />
        ) : null}
        {node?.facilitatorNotes ? (
          <div class="card signal tight small">
            <strong>{t("host.console.facilitatorNotes")}:</strong> {node.facilitatorNotes}
          </div>
        ) : null}
        {node && Object.keys(node.roleNotes).length ? (
          <details class="disclosure">
            <summary>
              <Icon name="lock" /> {t("host.console.roleNotes")}
            </summary>
            <ul class="small">
              {Object.entries(node.roleNotes).map(([roleId, note]) => (
                <li key={roleId}>
                  <strong>{scenario.roles.find((r) => r.id === roleId)?.name ?? roleId}:</strong> {note}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </Card>

      <div class="console-controls stack-sm">
        <div class="row">
          {state.status !== "ended" ? (
            <Button
              size="lg"
              icon={state.status === "lobby" ? "play" : "skip-forward"}
              onClick={primary}
              disabled={state.status === "debrief"}
            >
              {primaryLabel}
            </Button>
          ) : null}
          {state.status === "live" ? (
            <>
              <Button
                variant="secondary"
                icon="skip-back"
                onClick={() => command({ action: "back" })}
                disabled={!state.run || state.run.path.length < 2}
              >
                {t("host.console.back")}
              </Button>
              {node?.type === "call" && state.revealedLines < node.transcript.length ? (
                <Button variant="secondary" onClick={() => command({ action: "revealAllLines" })}>
                  {t("host.console.revealAll")}
                </Button>
              ) : null}
              {vote && !vote.result ? (
                <>
                  <Button
                    variant="secondary"
                    icon="timer"
                    onClick={() => command({ action: "extendVote", seconds: 30 })}
                  >
                    {t("host.console.extend")}
                  </Button>
                  <Button variant="secondary" onClick={() => setForceOpen(true)}>
                    {t("host.console.force")}
                  </Button>
                </>
              ) : null}
              <Button
                variant="ghost"
                icon={state.paused ? "play" : "pause"}
                onClick={() => command({ action: "pause", paused: !state.paused })}
              >
                {state.paused ? t("host.console.resume") : t("host.console.pause")}
              </Button>
            </>
          ) : null}
          <span class="grow" />
          {state.status !== "ended" ? (
            <Button variant="danger" icon="phone-off" onClick={() => setConfirm("end")}>
              {t("host.console.end")}
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => setConfirm("reopen")}>
              {t("host.console.reopen")}
            </Button>
          )}
        </div>
        {state.status === "debrief" ? <p class="small muted">{t("host.console.debriefReady")}</p> : null}
      </div>

      {confirm ? (
        <Dialog
          title={confirm === "end" ? t("host.console.end") : t("host.console.reopen")}
          onClose={() => setConfirm(null)}
        >
          <p>{confirm === "end" ? t("host.console.endConfirm") : t("host.console.reopenConfirm")}</p>
          <div class="row">
            <Button
              variant={confirm === "end" ? "danger" : "primary"}
              onClick={() => {
                command({ action: confirm === "end" ? "end" : "reopenLobby" });
                setConfirm(null);
              }}
            >
              {t("action.confirm")}
            </Button>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              {t("action.cancel")}
            </Button>
          </div>
        </Dialog>
      ) : null}

      {forceOpen && node?.type === "decision" ? (
        <Dialog title={t("host.console.force")} onClose={() => setForceOpen(false)}>
          <p class="muted small">{t("host.console.forceHelp")}</p>
          <div class="choice-list">
            {node.options.map((o) => (
              <button
                key={o.id}
                type="button"
                class="choice"
                onClick={() => {
                  command({ action: "forceOutcome", optionId: o.id });
                  setForceOpen(false);
                }}
              >
                <div class="grow">
                  <div class="title">{o.label}</div>
                  <div class="sub">{o.feedback}</div>
                </div>
                <QualityPill quality={o.quality} />
              </button>
            ))}
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}

function NodeView({
  node,
  state,
  vote,
  poll,
  now,
  scenario,
}: {
  node: ScenarioNode;
  state: S;
  vote: S["vote"];
  poll: S["poll"];
  now: number;
  scenario: Scenario;
}) {
  const { t } = useI18n();
  switch (node.type) {
    case "narrative":
      return (
        <>
          <h2>{node.title}</h2>
          <p style={{ whiteSpace: "pre-line" }}>{node.body}</p>
        </>
      );
    case "call":
      return (
        <>
          <h2>{node.title}</h2>
          <PhoneCall
            node={{
              type: "call",
              title: node.title,
              caller: node.caller,
              callee: { name: node.callee.name, roleId: node.callee.roleId ?? null },
              transcript: node.transcript.map((l) => ({
                speaker: l.speaker,
                name: l.name ?? null,
                text: l.text,
                pauseMs: l.pauseMs ?? null,
              })),
            }}
            revealedLines={node.transcript.length}
            reveal={{
              type: "call",
              caller: node.caller,
              redFlags: node.redFlags,
              tells: node.transcript
                .map((l, i) => ({ index: i, tell: l.tell ?? null }))
                .filter((x) => x.tell),
            }}
            compact
            animate={false}
          />
          <p class="muted small">
            {state.revealedLines}/{node.transcript.length}
          </p>
        </>
      );
    case "decision": {
      const d = node as DecisionNode;
      return (
        <>
          <div class="row between">
            <h2 style={{ margin: 0 }}>{d.prompt}</h2>
            {vote && !vote.result ? (
              <TimerRing closesAt={vote.closesAt} durationSec={vote.durationSec} now={now} size={80} />
            ) : null}
          </div>
          {d.context ? <p class="fg-2 small">{d.context}</p> : null}
          {vote ? (
            <p class="small">
              {t("host.console.votesProgress", { cast: vote.votesCast, eligible: vote.eligible })}
              {!state.settings.liveTally && !vote.result ? (
                <span class="muted"> · {t("host.console.tallyHidden")}</span>
              ) : null}
            </p>
          ) : null}
          <VoteBars
            options={d.options.map((o) => ({ id: o.id, label: o.label, quality: o.quality }))}
            tally={vote?.tally ?? (vote ? null : {})}
            winnerId={vote?.result?.optionId ?? null}
            showQuality
          />
          {vote?.result ? (
            <div class="card sunken tight small">
              <strong>{d.options.find((o) => o.id === vote.result!.optionId)?.label}</strong> —{" "}
              {d.options.find((o) => o.id === vote.result!.optionId)?.feedback}
              {vote.result.overridden ? <Pill tone="signal">{t("host.console.force")}</Pill> : null}
            </div>
          ) : null}
          {vote && !vote.result && state.players.length ? (
            <details class="disclosure">
              <summary>{t("host.console.notVoted")}</summary>
              <div class="row small">
                {state.players
                  .filter((p) => !vote.votedPlayerIds.includes(p.id))
                  .map((p) => (
                    <span key={p.id} class="pill">
                      {p.avatar} {p.name}
                    </span>
                  ))}
              </div>
            </details>
          ) : null}
        </>
      );
    }
    case "poll":
      return (
        <>
          <div class="row between">
            <h2 style={{ margin: 0 }}>{node.prompt}</h2>
            {poll && !poll.closed ? (
              <TimerRing closesAt={poll.closesAt} durationSec={node.timerSec} now={now} size={80} />
            ) : null}
          </div>
          <VoteBars options={node.options} tally={poll?.tally ?? {}} />
        </>
      );
    case "reveal":
      return (
        <>
          <h2>{node.title}</h2>
          <p style={{ whiteSpace: "pre-line" }}>{node.body}</p>
          <ul>
            {node.lessons.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </>
      );
    case "debrief":
      return (
        <>
          <h2>{node.title}</h2>
          <p style={{ whiteSpace: "pre-line" }}>{node.body}</p>
          <Dimensions dimensions={scenario.dimensions} scores={state.dimensionScores} />
        </>
      );
    default:
      return <h2>{node.title}</h2>;
  }
}

function Side({
  state,
  scenario,
  command,
  detail,
  reload,
}: {
  state: S;
  scenario: Scenario;
  command: Cmd;
  detail: Detail;
  reload: () => Promise<void>;
}) {
  const { t, locale } = useI18n();
  const [tab, setTab] = useState<"players" | "map" | "injects" | "links" | "settings">("players");
  const tabs: { id: typeof tab; label: string; icon: string }[] = [
    { id: "players", label: t("host.console.players"), icon: "users" },
    { id: "injects", label: t("host.console.injects"), icon: "zap" },
    { id: "map", label: t("host.console.map"), icon: "route" },
    { id: "links", label: t("host.console.links"), icon: "qr-code" },
    { id: "settings", label: t("host.console.settings"), icon: "settings" },
  ];
  return (
    <aside class="stack">
      <Card class="tight stack-sm">
        <div class="row small muted">
          <span>
            <span class={`status-dot ${state.facilitatorConnected ? "on" : ""}`} /> {t("host.console.status")}
          </span>
          <span>{t("host.console.screens", { count: state.screenCount })}</span>
          <span>{t("host.console.viewers", { count: state.viewerCount })}</span>
        </div>
        {state.status !== "lobby" ? (
          <Dimensions dimensions={scenario.dimensions} scores={state.dimensionScores} />
        ) : null}
        {state.run && Object.keys(state.run.vars).length ? (
          <div class="row small">
            {Object.entries(state.run.vars).map(([k, v]) => (
              <span key={k} class="pill">
                {k}: <span class="mono">{v}</span>
              </span>
            ))}
          </div>
        ) : null}
      </Card>
      <div class="segmented" role="tablist" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
        {tabs.map((tb) => (
          <button
            key={tb.id}
            type="button"
            role="tab"
            aria-selected={tab === tb.id}
            onClick={() => setTab(tb.id)}
            title={tb.label}
          >
            <Icon name={tb.icon} />
            <span class="visually-hidden">{tb.label}</span>
          </button>
        ))}
      </div>
      {tab === "players" ? <PlayersPanel state={state} scenario={scenario} command={command} /> : null}
      {tab === "injects" ? <InjectsPanel state={state} scenario={scenario} command={command} /> : null}
      {tab === "map" ? <MapPanel state={state} scenario={scenario} command={command} /> : null}
      {tab === "links" ? <LinksPanel detail={detail} scenario={scenario} reload={reload} /> : null}
      {tab === "settings" ? <SettingsPanel state={state} detail={detail} command={command} /> : null}
      {state.startedAt ? (
        <p class="muted xs">
          {formatDateTime(locale, state.startedAt)}
          {state.endedAt ? ` – ${formatDateTime(locale, state.endedAt)}` : ""}
        </p>
      ) : null}
    </aside>
  );
}

function PlayersPanel({ state, scenario, command }: { state: S; scenario: Scenario; command: Cmd }) {
  const { t } = useI18n();
  const [kick, setKick] = useState<S["players"][number] | null>(null);
  return (
    <Card class="tight">
      <h3 style={{ fontSize: "var(--text-md)" }}>
        {t("host.console.players")} ({state.playerCount})
      </h3>
      {state.players.length === 0 ? <p class="muted small">{t("host.console.noPlayers")}</p> : null}
      {state.players.map((p) => (
        <div key={p.id} class="player-row">
          <span style={{ fontSize: "1.4rem" }} aria-hidden="true">
            {p.avatar}
          </span>
          <div>
            <div class="row" style={{ gap: 6 }}>
              <strong>
                <bdi>{p.name}</bdi>
              </strong>
              {p.simulated ? (
                <SimMark />
              ) : (
                <span
                  class={`status-dot ${p.connected ? "on" : ""}`}
                  title={p.connected ? t("host.console.connected") : t("host.console.disconnected")}
                />
              )}
              <span class="mono small muted">{p.score}</span>
            </div>
            <select
              class="input small"
              aria-label={t("host.console.assignRole")}
              value={p.roleId ?? ""}
              onChange={(e) =>
                command({
                  action: "assignRole",
                  playerId: p.id,
                  roleId: (e.currentTarget as HTMLSelectElement).value || null,
                })
              }
            >
              <option value="">{t("player.lobby.noRole")}</option>
              {scenario.roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="ghost"
            size="sm"
            icon="x"
            aria-label={t("host.console.kick")}
            onClick={() => setKick(p)}
          />
        </div>
      ))}
      <SimulatedControls state={state} command={command} />
      {kick ? (
        <Dialog title={t("host.console.kick")} onClose={() => setKick(null)}>
          <p>{t("host.console.kickConfirm", { name: kick.name })}</p>
          <div class="row">
            <Button
              variant="danger"
              onClick={() => {
                command({ action: "kick", playerId: kick.id });
                setKick(null);
              }}
            >
              {t("host.console.kick")}
            </Button>
            <Button variant="ghost" onClick={() => setKick(null)}>
              {t("action.cancel")}
            </Button>
          </div>
        </Dialog>
      ) : null}
    </Card>
  );
}

/**
 * Simulated participants: artificial players that fill roles and vote, poll and react on their own,
 * so one person can demonstrate the whole exercise. Everything they do is decided server-side from a
 * seed; this panel only asks for them.
 */
function SimulatedControls({ state, command }: { state: S; command: Cmd }) {
  const { t } = useI18n();
  const [count, setCount] = useState(8);
  const [temperament, setTemperament] = useState<SimulationTemperament>("mixed");
  const [pace, setPace] = useState<SimulationPace>("natural");
  const remaining = MAX_SIMULATED_PER_SESSION - state.simulatedCount;
  const disabled = state.status === "ended" || remaining <= 0;
  return (
    <details class="disclosure sim-controls">
      <summary>
        <Icon name="bot" /> {t("host.sim.title")}
        {state.simulatedCount ? <> ({state.simulatedCount})</> : null}
      </summary>
      <div class="stack-sm">
        <p class="muted small" style={{ margin: 0 }}>
          {t("host.sim.help")}
        </p>
        <div class="sim-fields">
          <Field label={t("host.sim.count")} htmlFor="sim-count">
            <input
              id="sim-count"
              class="input"
              type="number"
              min={1}
              max={Math.max(1, remaining)}
              value={count}
              onInput={(e) => setCount(Number((e.currentTarget as HTMLInputElement).value) || 1)}
            />
          </Field>
          <Field label={t("host.sim.temperament")} htmlFor="sim-temperament">
            <select
              id="sim-temperament"
              class="input"
              value={temperament}
              onChange={(e) =>
                setTemperament((e.currentTarget as HTMLSelectElement).value as SimulationTemperament)
              }
            >
              {SIMULATION_TEMPERAMENTS.map((x) => (
                <option key={x} value={x}>
                  {t(`host.sim.temperament.${x}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("host.sim.pace")} htmlFor="sim-pace">
            <select
              id="sim-pace"
              class="input"
              value={pace}
              onChange={(e) => setPace((e.currentTarget as HTMLSelectElement).value as SimulationPace)}
            >
              <option value="natural">{t("host.sim.pace.natural")}</option>
              <option value="quick">{t("host.sim.pace.quick")}</option>
            </select>
          </Field>
        </div>
        <p class="muted xs" style={{ margin: 0 }}>
          {t(`host.sim.temperament.${temperament}.help`)}
        </p>
        <div class="row">
          <Button
            size="sm"
            icon="bot"
            disabled={disabled}
            onClick={() =>
              command({
                action: "addSimulated",
                count: Math.max(1, Math.min(remaining, count)),
                temperament,
                pace,
              })
            }
          >
            {t("host.sim.add")}
          </Button>
          {state.simulatedCount ? (
            <Button
              size="sm"
              variant="ghost"
              icon="trash"
              onClick={() => command({ action: "removeSimulated" })}
              disabled={state.status === "ended"}
            >
              {t("host.sim.removeAll", { count: state.simulatedCount })}
            </Button>
          ) : null}
        </div>
      </div>
    </details>
  );
}

function InjectsPanel({ state, scenario, command }: { state: S; scenario: Scenario; command: Cmd }) {
  const { t } = useI18n();
  return (
    <Card class="tight stack-sm">
      <h3 style={{ fontSize: "var(--text-md)" }}>{t("host.console.injects")}</h3>
      {scenario.injects.length === 0 || state.status !== "live" ? (
        <p class="muted small">{t("host.console.noInjects")}</p>
      ) : null}
      {scenario.injects.map((inj) => {
        const available = state.availableInjects.includes(inj.id);
        const fired = state.run?.injectsFired.includes(inj.id);
        return (
          <div key={inj.id} class="card sunken tight stack-sm" style={{ opacity: available ? 1 : 0.6 }}>
            <div class="row between">
              <strong>{inj.title}</strong>
              {fired ? <Pill>{t("action.done")}</Pill> : null}
            </div>
            <p class="small" style={{ margin: 0 }}>
              {inj.description}
            </p>
            {available ? (
              <Button
                size="sm"
                variant="signal"
                icon="zap"
                onClick={() => command({ action: "inject", injectId: inj.id })}
              >
                {t("host.console.fireInject")}
              </Button>
            ) : null}
          </div>
        );
      })}
    </Card>
  );
}

function MapPanel({ state, scenario, command }: { state: S; scenario: Scenario; command: Cmd }) {
  const { t } = useI18n();
  const [confirm, setConfirm] = useState<string | null>(null);
  const order = useMemo(() => {
    // Breadth-first from start so the map reads top-down; injects appended.
    const seen = new Set<string>();
    const out: string[] = [];
    const q = [scenario.start];
    while (q.length) {
      const id = q.shift()!;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(id);
      const n = scenario.nodes[id];
      if (n) for (const s of nodeSuccessors(n)) if (!seen.has(s)) q.push(s);
    }
    for (const id of Object.keys(scenario.nodes)) if (!seen.has(id)) out.push(id);
    return out;
  }, [scenario]);
  return (
    <Card class="tight stack-sm">
      <h3 style={{ fontSize: "var(--text-md)" }}>{t("host.console.map")}</h3>
      <div class="node-map">
        {order.map((id) => {
          const n = scenario.nodes[id]!;
          const visited = state.run?.path.includes(id);
          return (
            <button
              key={id}
              type="button"
              class={`${state.nodeId === id ? "current" : ""} ${visited && state.nodeId !== id ? "visited" : ""}`}
              disabled={state.status !== "live"}
              onClick={() => setConfirm(id)}
              title={t("host.console.goto")}
            >
              <span class="node-type">{n.type}</span>
              <span>{n.title}</span>
              {visited ? <Icon name="check" /> : <span />}
            </button>
          );
        })}
      </div>
      {confirm ? (
        <Dialog title={t("host.console.goto")} onClose={() => setConfirm(null)}>
          <p>{t("host.console.gotoConfirm", { title: scenario.nodes[confirm]?.title ?? confirm })}</p>
          <div class="row">
            <Button
              onClick={() => {
                command({ action: "goto", nodeId: confirm });
                setConfirm(null);
              }}
            >
              {t("host.console.goto")}
            </Button>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              {t("action.cancel")}
            </Button>
          </div>
        </Dialog>
      ) : null}
    </Card>
  );
}

function LinksPanel({
  detail,
  scenario,
  reload,
}: {
  detail: Detail;
  scenario: Scenario;
  reload: () => Promise<void>;
}) {
  const { t } = useI18n();
  const [label, setLabel] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [seatError, setSeatError] = useState<string | null>(null);
  const addSeat = async () => {
    if (!label.trim()) return;
    setBusy(true);
    setSeatError(null);
    try {
      await post(`/api/sessions/${detail.session.id}/seats`, {
        label: label.trim(),
        email: email.trim() || null,
        roleId: roleId || null,
      });
      setLabel("");
      setEmail("");
      await reload();
    } catch {
      setSeatError(t("host.console.seatEmailInvalid"));
    } finally {
      setBusy(false);
    }
  };
  const links: [string, string][] = [
    [t("host.console.joinLink"), detail.links.join!],
    [t("host.console.screenLink"), detail.links.screen!],
    [t("host.console.watchLink"), detail.links.watch!],
    [t("host.console.reportLink"), detail.links.report!],
  ];
  return (
    <div class="stack">
      <Card class="tight stack-sm">
        <h3 style={{ fontSize: "var(--text-md)" }}>{t("host.console.links")}</h3>
        {links.map(([name, url]) => (
          <div key={name} class="row between" style={{ gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div class="small" style={{ fontWeight: 700 }}>
                {name}
              </div>
              <div
                class="xs mono muted"
                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {url}
              </div>
            </div>
            <CopyButton text={url} />
          </div>
        ))}
      </Card>
      <Card class="tight stack-sm">
        <h3 style={{ fontSize: "var(--text-md)" }}>{t("host.console.seats")}</h3>
        <p class="muted small">{t("host.console.seatsHelp")}</p>
        {detail.seats.map((s) => (
          <div key={s.id} class="row between" style={{ gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div class="small" style={{ fontWeight: 700 }}>
                {s.label}{" "}
                <span class="muted">
                  · {scenario.roles.find((r) => r.id === s.roleId)?.name ?? t("player.lobby.noRole")}
                </span>
              </div>
              <div class="xs muted">
                {s.claimedBy ? t("host.console.seatClaimed") : t("host.console.seatOpen")}
                {s.email ? <span class="mono"> · {s.email}</span> : null}
              </div>
            </div>
            <div class="row" style={{ gap: 4 }}>
              <CopyButton text={s.link} />
              <Button
                variant="ghost"
                size="sm"
                icon="trash"
                aria-label={t("action.delete")}
                onClick={async () => {
                  await del(`/api/sessions/${detail.session.id}/seats/${s.id}`);
                  await reload();
                }}
              />
            </div>
          </div>
        ))}
        <div class="stack-sm">
          <Field label={t("host.console.seatLabel")} htmlFor="seat-label">
            <input
              id="seat-label"
              class="input"
              value={label}
              onInput={(e) => setLabel((e.currentTarget as HTMLInputElement).value)}
              maxLength={80}
            />
          </Field>
          <Field
            label={t("host.console.seatEmail")}
            htmlFor="seat-email"
            help={t("host.console.seatEmailHelp")}
            error={seatError}
          >
            <input
              id="seat-email"
              class="input"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder={t("host.console.seatEmailPlaceholder")}
              value={email}
              onInput={(e) => {
                setEmail((e.currentTarget as HTMLInputElement).value);
                setSeatError(null);
              }}
              maxLength={254}
            />
          </Field>
          <Field label={t("host.console.seatRole")} htmlFor="seat-role">
            <select
              id="seat-role"
              class="input"
              value={roleId}
              onChange={(e) => setRoleId((e.currentTarget as HTMLSelectElement).value)}
            >
              <option value="">{t("player.lobby.noRole")}</option>
              {scenario.roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
          <Button size="sm" icon="plus" disabled={busy || !label.trim()} onClick={addSeat}>
            {t("host.console.addSeat")}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function SettingsPanel({ state, detail, command }: { state: S; detail: Detail; command: Cmd }) {
  const { t } = useI18n();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const onChange = async (p: Partial<SessionSettings>) => {
    await command({ action: "updateSettings", settings: p });
    patch(`/api/sessions/${detail.session.id}`, { settings: p }).catch(() => undefined);
  };
  return (
    <Card class="tight stack">
      <h3 style={{ fontSize: "var(--text-md)" }}>{t("host.console.settings")}</h3>
      <SettingsForm settings={state.settings} onChange={onChange} />
      <hr />
      <Button variant="danger" size="sm" icon="trash" onClick={() => setConfirmDelete(true)}>
        {t("host.console.deleteSession")}
      </Button>
      {confirmDelete ? (
        <Dialog title={t("host.console.deleteSession")} onClose={() => setConfirmDelete(false)}>
          <p>{t("host.console.deleteConfirm")}</p>
          <div class="row">
            <Button
              variant="danger"
              onClick={async () => {
                await del(`/api/sessions/${detail.session.id}`);
                navigate("/host");
              }}
            >
              {t("action.delete")}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              {t("action.cancel")}
            </Button>
          </div>
        </Dialog>
      ) : null}
    </Card>
  );
}

export { hasIcon };
